// src/pages/productPassportHelpers.js
// Pure utility functions for ProductPassport — no React, no state

// ── Label formatting ──────────────────────────────────────────────────────────
export const formatLabel = (key) => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

// ── Type checks ───────────────────────────────────────────────────────────────
export const isNestedObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export const isArrayOfObjects = (value) =>
  Array.isArray(value) && value.length > 0 && typeof value[0] === 'object';

export const isURL = (str) => {
  if (typeof str !== 'string') return false;
  return str.startsWith('http://') || str.startsWith('https://') || str.startsWith('www.');
};

// ── Value formatting ──────────────────────────────────────────────────────────
export const tryParseJSON = (value) => {
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

export const capitalizeValue = (value) => {
  if (value === null || value === undefined) return 'N/A';
  const strValue = String(value);
  if (isURL(strValue)) return strValue;
  const isAllLower = strValue === strValue.toLowerCase();
  const isAllUpper = strValue === strValue.toUpperCase();
  if (isAllLower || isAllUpper) {
    return strValue
      .split(' ')
      .map(word => (word.length === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()))
      .join(' ');
  }
  return strValue;
};

export const formatValue = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) {
    if (value.length === 0) return 'N/A';
    if (typeof value[0] !== 'object') return value.join(', ');
    return JSON.stringify(value);
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return capitalizeValue(value);
};

// ── Field ordering ────────────────────────────────────────────────────────────
export const fieldOrderMap = {
  identification: [
    'dppId',
    'serialNumber',
    'manufacturerIdentifier',
    'manufacturingFacilityID',
    'productCategory',
    'manufacturerName',
    'productionDate',
    'productLevel'
  ]
};

export const sortFields = (fields, tabId) => {
  if (!fieldOrderMap[tabId]) return fields;
  const order = fieldOrderMap[tabId];
  const orderedFields = [];
  const remainingFields = [...fields];
  order.forEach(fieldKey => {
    const idx = remainingFields.findIndex(f =>
      f.label.toLowerCase().replace(/\s+/g, '') === fieldKey.toLowerCase().replace(/\s+/g, '')
    );
    if (idx !== -1) {
      orderedFields.push(remainingFields[idx]);
      remainingFields.splice(idx, 1);
    }
  });
  return [...orderedFields, ...remainingFields];
};

// ── Tab mapping ───────────────────────────────────────────────────────────────
export const apiToTabMapping = {
  identification: 'identification',
  productInformation: 'productInfo',
  durabilityAndRepair: 'durability',
  materialComposition: 'materialComposition',
  substancesOfConcern: 'substancesOfConcern',
  economicOperators: 'economicOperators',
  warranty: 'warranty',
  sustainability: 'sustainability',
  environmentalFootprint: 'environmental',
  televisionSpecifications: 'tvSpecs',
  operatingConditions: 'operatingSoftware',
  softwareInformation: 'operatingSoftware',
  safetyAndRisk: 'safetyConsumer',
  consumerInformation: 'safetyConsumer',
  endOfLife: 'endoflife',
  lifecycle: 'lifecycleCompliance',
  compliance: 'lifecycleCompliance'
};

// ── Flatten category data into displayable field entries ──────────────────────
export const flattenCategoryData = (data) => {
  const fields = [];

  Object.entries(data).forEach(([key, value]) => {

    // Special handling: physicalDimension → single formatted string
    if (key === 'physicalDimension' && isNestedObject(value)) {
  fields.push({
    label: formatLabel(key),
    value,
    isNested: true,
    fieldKey: key,
    isEditable: true,
    nestedFields: [
      { label: 'Width (mm)',   value: formatValue(value.width),  nestedKey: 'width',  isEditable: true },
      { label: 'Height (mm)',  value: formatValue(value.height), nestedKey: 'height', isEditable: true },
      { label: 'Length (mm)',  value: formatValue(value.length), nestedKey: 'length', isEditable: true },
      { label: 'Volume (mm³)', value: formatValue(value.volume), nestedKey: 'volume', isEditable: true },
      { label: 'Weight (g)',   value: formatValue(value.weight), nestedKey: 'weight', isEditable: true },
    ].filter(f => f.value !== 'N/A')
  });
  return;
}
    // Parse JSON strings
    let parsedValue = tryParseJSON(value);
    if (parsedValue !== null) value = parsedValue;

    if (isArrayOfObjects(value)) {
      // Array of objects → nested box with items
      fields.push({
        label: formatLabel(key),
        value,
        isNested: true,
        isArray: true,
        fieldKey: key,
        isEditable: true,
        arrayItems: value.map((item, index) => ({
          label: `Item ${index + 1}`,
          arrayIndex: index,
          nestedFields: Object.entries(item).map(([nk, nv]) => ({
            label: formatLabel(nk),
            value: formatValue(nv),
            nestedKey: nk,
            isEditable: true
          }))
        }))
      });

    } else if (isNestedObject(value)) {
      const entries = Object.entries(value);
      const hasDeepNesting = entries.some(([, v]) => isNestedObject(v) || isArrayOfObjects(v));

      if (!hasDeepNesting) {
        // Shallow object → single nested box
        fields.push({
          label: formatLabel(key),
          value,
          isNested: true,
          fieldKey: key,
          isEditable: true,
          nestedFields: entries.map(([nk, nv]) => ({
            label: formatLabel(nk),
            value: formatValue(nv),
            nestedKey: nk,
            isEditable: true
          }))
        });

      } else {
        // Deep object → expand sub-keys into separate boxes
        entries.forEach(([subKey, subValue]) => {
          let parsedSub = tryParseJSON(subValue);
          if (parsedSub !== null) subValue = parsedSub;

          if (isArrayOfObjects(subValue)) {
            fields.push({
              label: `${formatLabel(key)} – ${formatLabel(subKey)}`,
              value: subValue,
              isNested: true,
              isArray: true,
              fieldKey: `${key}.${subKey}`,
              isEditable: true,
              arrayItems: subValue.map((item, index) => ({
                label: `Item ${index + 1}`,
                arrayIndex: index,
                nestedFields: Object.entries(item).map(([nk, nv]) => ({
                  label: formatLabel(nk),
                  value: formatValue(nv),
                  nestedKey: nk,
                  isEditable: true
                }))
              }))
            });

          } else if (isNestedObject(subValue)) {
            const subEntries = Object.entries(subValue);
            const subHasDeepNesting = subEntries.some(([, v]) => isNestedObject(v));

            if (subHasDeepNesting) {
              // 3rd level (e.g. componentDetails.mainBoard.{type,...})
              subEntries.forEach(([subSubKey, subSubValue]) => {
                if (isNestedObject(subSubValue)) {
                  fields.push({
                    label: `${formatLabel(subKey)} – ${formatLabel(subSubKey)}`,
                    value: subSubValue,
                    isNested: true,
                    fieldKey: `${key}.${subKey}.${subSubKey}`,
                    isEditable: true,
                    nestedFields: Object.entries(subSubValue).map(([nk, nv]) => ({
                      label: formatLabel(nk),
                      value: formatValue(nv),
                      nestedKey: nk,
                      isEditable: true
                    }))
                  });
                } else {
                  fields.push({
                    label: `${formatLabel(subKey)} – ${formatLabel(subSubKey)}`,
                    value: formatValue(subSubValue),
                    isNested: false,
                    fieldKey: `${key}.${subKey}.${subSubKey}`,
                    isEditable: true
                  });
                }
              });

            } else {
              fields.push({
                label: `${formatLabel(key)} – ${formatLabel(subKey)}`,
                value: subValue,
                isNested: true,
                fieldKey: `${key}.${subKey}`,
                isEditable: true,
                nestedFields: subEntries.map(([nk, nv]) => ({
                  label: formatLabel(nk),
                  value: formatValue(nv),
                  nestedKey: nk,
                  isEditable: true
                }))
              });
            }

          } else {
            fields.push({
              label: `${formatLabel(key)} – ${formatLabel(subKey)}`,
              value: formatValue(subValue),
              isNested: false,
              fieldKey: `${key}.${subKey}`,
              isEditable: true
            });
          }
        });
      }

    } else {
      fields.push({
        label: formatLabel(key),
        value: formatValue(value),
        isNested: false,
        fieldKey: key,
        isEditable: true
      });
    }
  });

  return fields;
};